import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const CalculatorContainer = styled.div`
  padding: 20px;
  background-color: #1E1E1E;
  color: white;
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;
`;

const Display = styled.div`
  background-color: #000;
  border: 1px solid #d1eaff;
  padding: 10px 15px;
  border-radius: 4px;
  margin-bottom: 15px;
  text-align: right;
  height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
`;

const Expression = styled.div`
  font-size: 14px;
  color: #888;
  min-height: 20px;
  width: 100%;
`;

const Result = styled.div`
  font-size: ${(props) => props.fontSize}px;
  line-height: 1.2;
  width: 100%;
`;

const ButtonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
`;

const Button = styled.button`
  padding: 12px;
  font-size: 16px;
  border: 1px solid #555;
  border-radius: 4px;
  background-color: #444;
  color: white;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;

  &:hover {
    background-color: #555;
  }
`;

const ScientificCalculator = () => {
  const [displayValue, setDisplayValue] = useState('0');
  const [expression, setExpression] = useState('');
  const [fontSize, setFontSize] = useState(32);

  useEffect(() => {
    if (displayValue.length > 16) {
      setFontSize(20);
    } else if (displayValue.length > 12) {
      setFontSize(24);
    } else if (displayValue.length > 8) {
      setFontSize(28);
    } else {
      setFontSize(32);
    }
  }, [displayValue]);

  const handleButtonClick = (value) => {
    const MAX_DIGITS = 13;

    if (value === '=') {
      try {
        // Get the expression without the trailing operator
        const expressionToEvaluate = expression + displayValue;
        let result = eval(expressionToEvaluate); // Using eval instead of new Function
        
        if (Math.abs(result) > 1e10 || (Math.abs(result) < 1e-10 && result !== 0)) {
          result = Number(result).toExponential(3).replace(/\.?0+e/, 'e');
        }
        // Move the full expression to top row
        setExpression(expressionToEvaluate + ' =');
        // Show result in main display
        setDisplayValue(result.toString());
      } catch (error) {
        setDisplayValue('Error');
        setExpression('');
      }
    } else if (value === 'C') {
      setDisplayValue('0');
      setExpression('');
    } else if (value === '⌫') {
      setDisplayValue(displayValue.slice(0, -1) || '0');
    } else if (['+', '-', '*', '/'].includes(value)) {
      // Move current number to expression when operator is clicked
      setExpression(displayValue + ' ' + value + ' ');  // Added spaces around operator
      setDisplayValue('0');
    } else if (['1/x', 'x²', '√x'].includes(value)) {
      let result;
      let expressionText;
      
      try {
        if (value === '1/x') {
          result = 1 / parseFloat(displayValue);
          expressionText = `1/(${displayValue})`;
        } else if (value === 'x²') {
          result = Math.pow(parseFloat(displayValue), 2);
          expressionText = `sqr(${displayValue})`;
        } else if (value === '√x') {
          result = Math.sqrt(parseFloat(displayValue));
          expressionText = `√(${displayValue})`;
        }

        if (Math.abs(result) > 1e10 || (Math.abs(result) < 1e-10 && result !== 0)) {
          result = Number(result).toExponential(3).replace(/\.?0+e/, 'e');
        }
        
        setExpression(expressionText + ' =');
        setDisplayValue(result.toString());
      } catch {
        setDisplayValue('Error');
      }
    } else if (value === '+/-') {
      setDisplayValue((parseFloat(displayValue) * -1).toString());
    } else {
      // For number inputs, check if we're below max digits
      if (displayValue.length < MAX_DIGITS || displayValue === '0') {
        setDisplayValue(displayValue === '0' ? value : displayValue + value);
      }
    }
  };

  return (
    <CalculatorContainer>
      <Display>
        <Expression>{expression}</Expression>
        <Result fontSize={fontSize}>{displayValue}</Result>
      </Display>
      <ButtonGrid>
        {['%', 'CE', 'C', '⌫', '1/x', 'x²', '√x', '/', '7', '8', '9', '*', '4', '5', '6', '-', '1', '2', '3', '+', '+/-', '0', '.', '='].map((btn) => (
          <Button key={btn} onClick={() => handleButtonClick(btn)}>{btn}</Button>
        ))}
      </ButtonGrid>
    </CalculatorContainer>
  );
};

export default ScientificCalculator; 